import React, { useMemo, useState, useEffect } from 'react';
import { Filter, Layers, ListTree, Search, Server, Workflow } from 'lucide-react';
import DashboardNav, { DashboardBrandRow } from './DashboardNav';
import { mcpApiUrl } from './mcpApi';

// Fetch the MCP tool catalog from the HTTP shim (hierarchical view).
// Relative path uses CRA proxy in dev; use REACT_APP_MCP_API_BASE for static hosting.
const TOOLS_CATALOG_URL = mcpApiUrl('/v1/tools/catalog?include_details=true&hierarchy=true');

// helper: convert ToolMeta -> display entry
function toolMetaToEntry(cat, m) {
  const service = (m.namespace || m.Namespace) && (m.namespace || m.Namespace).length > 0 ? (m.namespace || m.Namespace)[0] : '';
  const paramSchema = m.param_schema || m.ParamSchema || {};
  const reqType = paramSchema ? Object.keys(paramSchema).join(', ') : '';
  const path = m.path || m.Path || m.tool || m.Tool;
  const description = m.description || m.Description || '';
  return {
    category: cat,
    service: service,
    method: 'TOOL',
    path: path,
    auth: '',
    mode: '',
    requestType: reqType,
    responseType: '',
    description: description,
    _raw: m,
  };
}

const METHOD_CLASS = {
  GET: 'text-cyan-300 bg-cyan-950/60 border-cyan-800',
  POST: 'text-amber-300 bg-amber-950/60 border-amber-800',
  WS: 'text-fuchsia-300 bg-fuchsia-950/60 border-fuchsia-800',
};

// categories and methods are derived from fetched catalog

const ApiCatalogDashboard = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [method, setMethod] = useState('All');
  const [catalogEntries, setCatalogEntries] = useState([]);
  const [catalogHierarchy, setCatalogHierarchy] = useState({});
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await fetch(TOOLS_CATALOG_URL);
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        // data should be map category -> hierarchical tree
        const entries = [];
        const hierarchy = {};
        const traverse = (cat, node) => {
          for (const k of Object.keys(node)) {
            if (k === '_tools') {
              const tools = node[k] || [];
              for (const m of tools) {
                entries.push(toolMetaToEntry(cat, m));
              }
            } else {
              const child = node[k];
              if (child && typeof child === 'object') {
                traverse(cat, child);
              }
            }
          }
        };
        for (const cat of Object.keys(data)) {
          const tree = data[cat];
          hierarchy[cat] = tree;
          traverse(cat, tree || {});
        }
        if (mounted) {
          setCatalogEntries(entries);
          setCatalogHierarchy(hierarchy);
        }
      } catch (err) {
        // attempt relative fetch as fallback
        try {
          const res2 = await fetch(mcpApiUrl('/v1/tools/catalog?include_details=true&hierarchy=false'));
          if (!res2.ok) return;
          const data2 = await res2.json();
          // fallback: flat map category -> []ToolMeta
          const entries = [];
          for (const cat of Object.keys(data2)) {
            const metas = data2[cat] || [];
            for (const m of metas) {
              entries.push(toolMetaToEntry(cat, m));
            }
          }
          if (mounted) setCatalogEntries(entries);
        } catch (err2) {
          console.warn('unable to load tools catalog', err, err2);
        }
      }
    };
    load();
    return () => { mounted = false };
  }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(catalogEntries.map((e) => e.category))).sort()], [catalogEntries]);
  const methods = useMemo(() => ['All', ...Array.from(new Set(catalogEntries.map((e) => e.method))).sort()], [catalogEntries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalogEntries.filter((entry) => {
      if (category !== 'All' && entry.category !== category) return false;
      if (method !== 'All' && entry.method !== method) return false;
      if (!q) return true;
      return [
        entry.category,
        entry.service,
        entry.method,
        entry.path,
        entry.requestType,
        entry.responseType,
        entry.description,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [category, method, query, catalogEntries]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-8 font-mono">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-slate-800 pb-4">
          <DashboardBrandRow />
          <DashboardNav />
        </div>
        <header className="rounded-2xl border border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.25em] text-slate-400 uppercase">Temporary Build Dashboard</p>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">API Catalog Tracker</h1>
              <p className="text-slate-300 mt-2 text-sm">Visual map of categories, endpoints, methods, auth modes, and request/response types.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 min-w-[260px]">
              <MetricCard icon={<Server size={16} />} label="Total Tools" value={String(catalogEntries.length)} />
              <MetricCard icon={<Layers size={16} />} label="Categories" value={String(Math.max(0, categories.length - 1))} />
              <MetricCard icon={<ListTree size={16} />} label="Visible" value={String(filtered.length)} />
              <MetricCard icon={<Workflow size={16} />} label="Services" value={String(new Set(catalogEntries.map((x) => x.service)).size)} />
            </div>
          </div>
        </header>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 md:p-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-3.5 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by endpoint, category, request type..."
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-9 py-2.5 text-sm outline-none focus:border-cyan-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-slate-400" />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm"
              >
                {methods.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          {Object.keys(grouped).length === 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-center text-slate-400">No endpoints match current filters.</div>
          )}

          {/* If no filters/search, render hierarchical tree per category */}
          {query.trim() === '' && method === 'All' && category === 'All' ? (
            Object.keys(catalogHierarchy)
              .sort()
              .map((cat) => (
                <div key={cat} className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/90 flex items-center justify-between">
                    <h2 className="text-sm uppercase tracking-wider text-slate-300">{cat}</h2>
                  </div>
                  <div className="p-4">
                    <TreeNode name={cat} node={catalogHierarchy[cat] || {}} />
                  </div>
                </div>
              ))
          ) : (
            Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([group, items]) => (
                <div key={group} className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/90 flex items-center justify-between">
                    <h2 className="text-sm uppercase tracking-wider text-slate-300">{group}</h2>
                    <span className="text-xs text-slate-400">{items.length} endpoint(s)</span>
                  </div>
                  <div className="divide-y divide-slate-800">
                    {items.map((entry) => (
                      <EndpointRow key={`${entry.method}-${entry.path}`} entry={entry} />
                    ))}
                  </div>
                </div>
              ))
          )}
        </section>
      </div>
    </div>
  );
};

const MetricCard = ({ icon, label, value }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2">
    <div className="flex items-center gap-2 text-slate-400 text-xs">{icon}<span>{label}</span></div>
    <div className="mt-1 text-xl font-semibold tracking-tight">{value}</div>
  </div>
);

const TreeNode = ({ name, node }) => {
  const [open, setOpen] = useState(false);
  if (!node || typeof node !== 'object') return null;
  const childKeys = Object.keys(node).filter((k) => k !== '_tools').sort();
  const tools = node['_tools'] || node._tools || [];
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="text-sm font-medium text-slate-200">{name}</div>
        <div className="text-xs text-slate-400">{open ? '▼' : '▶'}</div>
      </div>
      {open && (
        <div className="pl-4">
          {childKeys.map((k) => (
            <TreeNode key={k} name={k} node={node[k]} />
          ))}
          {Array.isArray(tools) && tools.map((t, idx) => (
            <div key={`tool-${idx}`} className="mt-2">
              <ToolRowFromMeta meta={t} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ToolRowFromMeta = ({ meta }) => {
  const [open, setOpen] = useState(false);
  const name = meta.tool || meta.Tool || meta.path || meta.Path || 'unknown';
  const desc = meta.description || meta.Description || '';
  const schema = meta.param_schema || meta.ParamSchema || null;
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-100">{name}</div>
          <div className="text-xs text-slate-400">{desc}</div>
        </div>
        <div>
          <button onClick={() => setOpen(!open)} className="text-xs text-cyan-300">{open ? 'Hide' : 'Details'}</button>
        </div>
      </div>
      {open && schema && (
        <pre className="mt-2 text-xs text-slate-300 overflow-auto max-h-48">{JSON.stringify(schema, null, 2)}</pre>
      )}
    </div>
  );
};

const EndpointRow = ({ entry }) => (
  <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">
    <div className="lg:col-span-2">
      <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-semibold ${METHOD_CLASS[entry.method] || 'text-slate-300 bg-slate-800 border-slate-700'}`}>
        {entry.method}
      </span>
      <div className="text-[11px] text-slate-400 mt-2">service: {entry.service}</div>
    </div>
    <div className="lg:col-span-4">
      <div className="text-sm font-semibold text-slate-100 break-all">{entry.path}</div>
      <p className="text-xs text-slate-400 mt-1">{entry.description}</p>
    </div>
    <div className="lg:col-span-3 text-xs text-slate-300">
      <div><span className="text-slate-500">Request:</span> {entry.requestType}</div>
      <div className="mt-1"><span className="text-slate-500">Response:</span> {entry.responseType}</div>
    </div>
    <div className="lg:col-span-3 flex flex-col gap-1 text-xs">
      <div><span className="text-slate-500">Auth:</span> <span className="text-slate-200">{entry.auth}</span></div>
      <div><span className="text-slate-500">Mode:</span> <span className="text-slate-200">{entry.mode}</span></div>
    </div>
  </div>
);

export default ApiCatalogDashboard;
